import {ApplicationConfig, provideBrowserGlobalErrorListeners, provideZoneChangeDetection} from '@angular/core';
import {provideRouter} from '@angular/router';

import {routes} from './navigation/app.routes';
import {HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi} from '@angular/common/http';
import {InteractionType, PublicClientApplication, LogLevel} from '@azure/msal-browser';
import {environment} from '../environments/environment';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard,
  MsalGuardConfiguration, MsalInterceptor,
  MsalInterceptorConfiguration, MsalRedirectComponent, MsalService
} from '@azure/msal-angular';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {PrimeNGConfigType, providePrimeNG} from 'primeng/config';
import CLCTheme from '@primeuix/themes/aura';
import {definePreset} from '@primeuix/themes';
import clctaTokens from './styletoken/clcta-theme.json';
import {provideApi} from '../api';
import {ErrorInterceptor} from '../util/error.interceptor';
import {ConfirmationService, MessageService} from 'primeng/api';
import {DialogService} from 'primeng/dynamicdialog';


export interface CLCTaEnvironment {
  appName: string;
  appVersion: string;
  tenantSettings: TenantSettings;
}


export interface TenantSettings {
  name: string;
  baseUrl: string;
  baseServerUrl: string;
  scopeUris: string[];
  tenantId: string;
  uiClientId: string;
  redirectUrl: string;
  authority?: string;
}

export function MSALInstanceFactory() {
  // console.log('** MSALInstanceFactory **');
  return new PublicClientApplication({
    auth: {
      clientId: environment.tenantSettings.uiClientId,
      redirectUri: environment.tenantSettings.redirectUrl,
      postLogoutRedirectUri: environment.tenantSettings.redirectUrl,
      authority: environment.tenantSettings.authority ?? '',
    },
    cache: {
      cacheLocation: 'localStorage',
    },
    // system: {
    //   allowPlatformBroker: false, // Disables WAM Broker
    //   loggerOptions: {
    //     loggerCallback,
    //     logLevel: LogLevel.Info,
    //     piiLoggingEnabled: false,
    //   },
    // }
  });
}

// force login before accessing protected routes}
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  // console.log('** MSALGuardConfigFactory **');
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: environment.tenantSettings.scopeUris,
    },
  };

}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  // console.log('##> MSALInterceptorConfigFactory <##');
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.tenantSettings.baseServerUrl, environment.tenantSettings.scopeUris);

  // console.log('** protectedResourceMap: ', protectedResourceMap);

  return {
    interactionType: InteractionType.Popup,
    protectedResourceMap,
  }

}

const ClctaTheme = definePreset(CLCTheme, {
  ...clctaTokens,
  components: {
    tabs: {
      tablist: {
        background: 'transparent'
      }
    }
  }
});
const appPrimeConfig: PrimeNGConfigType = {
  theme: {
    preset: ClctaTheme,
    options: {
      darkModeSelector: 'none'
    }
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    providePrimeNG(appPrimeConfig),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideApi(environment.tenantSettings.baseUrl),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory},
    {provide: MSAL_GUARD_CONFIG, useFactory:MSALGuardConfigFactory},
    {provide: MSAL_INTERCEPTOR_CONFIG, useFactory:MSALInterceptorConfigFactory},
    MsalService,
    MsalGuard,
    MsalRedirectComponent,
    MsalBroadcastService,
    ConfirmationService,
    MessageService,
    // Root-provided so ConfirmationWrapperService can open the cancel dialog. Components that
    // declare their own DialogService (view-ta, ta-audit-log-button) still shadow this one.
    DialogService,
  ]
};
